import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import Modal from './Modal';
import './AdminDashboard.css';

/* ═══════════════════════════════
   MOCK DATA
   ═══════════════════════════════ */

const trafficData = [
  { name: 'Jan', views: 22000, subs: 8500 },
  { name: 'Feb', views: 35000, subs: 12000 },
  { name: 'Mar', views: 48000, subs: 18000 },
  { name: 'Apr', views: 62000, subs: 24000 },
  { name: 'May', views: 58000, subs: 28000 },
  { name: 'Jun', views: 75000, subs: 32000 },
  { name: 'Jul', views: 88000, subs: 38000 },
  { name: 'Aug', views: 95000, subs: 42000 },
  { name: 'Sep', views: 82000, subs: 45000 },
  { name: 'Oct', views: 110000, subs: 52000 },
  { name: 'Nov', views: 125000, subs: 58000 },
  { name: 'Dec', views: 148000, subs: 64000 },
];

const planDistribution = [
  { name: 'Basic', value: 4200, color: '#6B7280' },
  { name: 'Gold', value: 2800, color: '#F59E0B' },
  { name: 'Diamond', value: 1500, color: '#06B6D4' },
  { name: 'Platinum', value: 850, color: '#8B5CF6' },
];

const revenueByPlan = [
  { name: 'Basic', revenue: 84000, subscribers: 4200 },
  { name: 'Gold', revenue: 196000, subscribers: 2800 },
  { name: 'Diamond', revenue: 225000, subscribers: 1500 },
  { name: 'Platinum', revenue: 212500, subscribers: 850 },
];

const recentUsers = [
  { id: 1, email: 'priya.sharma@gmail.com', plan: 'Platinum', date: '2026-04-09', status: 'active' },
  { id: 2, email: 'rahul.verma@outlook.com', plan: 'Gold', date: '2026-04-09', status: 'active' },
  { id: 3, email: 'deepak.patel@yahoo.com', plan: 'Diamond', date: '2026-04-08', status: 'active' },
  { id: 4, email: 'sneha.gupta@gmail.com', plan: 'Basic', date: '2026-04-08', status: 'active' },
  { id: 5, email: 'amit.kumar@gmail.com', plan: 'Gold', date: '2026-04-07', status: 'inactive' },
  { id: 6, email: 'ananya.reddy@outlook.com', plan: 'Platinum', date: '2026-04-07', status: 'active' },
  { id: 7, email: 'vikram.singh@gmail.com', plan: 'Diamond', date: '2026-04-06', status: 'active' },
  { id: 8, email: 'neha.jain@yahoo.com', plan: 'Basic', date: '2026-04-06', status: 'suspended' },
  { id: 9, email: 'kartik.mishra@gmail.com', plan: 'Gold', date: '2026-04-05', status: 'active' },
  { id: 10, email: 'pooja.nair@outlook.com', plan: 'Basic', date: '2026-04-05', status: 'active' },
];

const allUsers = [
  ...recentUsers,
  { id: 11, email: 'suresh.iyer@gmail.com', plan: 'Gold', date: '2026-04-04', status: 'active' },
  { id: 12, email: 'kaviya.menon@yahoo.com', plan: 'Basic', date: '2026-04-04', status: 'active' },
  { id: 13, email: 'rajan.choudhury@gmail.com', plan: 'Diamond', date: '2026-04-03', status: 'inactive' },
  { id: 14, email: 'meera.das@outlook.com', plan: 'Platinum', date: '2026-04-03', status: 'active' },
  { id: 15, email: 'arjun.pillai@gmail.com', plan: 'Gold', date: '2026-04-02', status: 'active' },
  { id: 16, email: 'divya.saxena@yahoo.com', plan: 'Basic', date: '2026-04-02', status: 'suspended' },
  { id: 17, email: 'rohit.agarwal@gmail.com', plan: 'Diamond', date: '2026-04-01', status: 'active' },
  { id: 18, email: 'shreya.bose@outlook.com', plan: 'Gold', date: '2026-04-01', status: 'active' },
  { id: 19, email: 'manoj.tiwari@gmail.com', plan: 'Basic', date: '2026-03-31', status: 'active' },
  { id: 20, email: 'rashmi.joshi@yahoo.com', plan: 'Platinum', date: '2026-03-31', status: 'active' },
];

const expiringUsers = [
  { email: 'amit.kumar@gmail.com', plan: 'Gold', expiresIn: '2 days' },
  { email: 'neha.jain@yahoo.com', plan: 'Basic', expiresIn: '3 days' },
  { email: 'rajan.choudhury@gmail.com', plan: 'Diamond', expiresIn: '5 days' },
  { email: 'divya.saxena@yahoo.com', plan: 'Basic', expiresIn: '7 days' },
];

const deviceData = [
  { name: 'Mobile', value: 62, color: '#8B5CF6' },
  { name: 'Desktop', value: 28, color: '#06B6D4' },
  { name: 'Tablet', value: 10, color: '#F59E0B' },
];

const cityData = [
  { city: 'Mumbai', users: 2840, pct: 30 },
  { city: 'Delhi', users: 1890, pct: 20 },
  { city: 'Bengaluru', users: 1510, pct: 16 },
  { city: 'Hyderabad', users: 950, pct: 10 },
  { city: 'Chennai', users: 760, pct: 8 },
  { city: 'Pune', users: 570, pct: 6 },
  { city: 'Others', users: 830, pct: 10 },
];

const featureUsage = [
  { name: 'Sector Rotation', icon: '📊', count: '8.2K' },
  { name: 'Intraday Scan', icon: '📈', count: '7.5K' },
  { name: 'Momentum Radar', icon: '📡', count: '6.1K' },
  { name: 'Heatmap', icon: '🔥', count: '5.8K' },
  { name: 'OI Monitor', icon: '📊', count: '4.3K' },
  { name: 'Swing Zone', icon: '🎯', count: '3.9K' },
  { name: 'Traders Diary', icon: '📖', count: '2.7K' },
  { name: 'SIP Calculator', icon: '📱', count: '2.1K' },
];

const peakHours = [
  { hour: '9 AM', usage: 85 }, { hour: '10 AM', usage: 95 },
  { hour: '11 AM', usage: 78 }, { hour: '12 PM', usage: 55 },
  { hour: '1 PM', usage: 42 }, { hour: '2 PM', usage: 68 },
  { hour: '3 PM', usage: 92 }, { hour: '4 PM', usage: 45 },
];

/* Sparkline data for stat cards */
const sparklines = {
  users: [4, 6, 5, 8, 7, 9, 8, 10, 9, 12],
  subs: [3, 5, 4, 7, 6, 8, 9, 8, 10, 9],
  revenue: [5, 4, 7, 6, 8, 7, 9, 11, 10, 12],
  views: [6, 8, 7, 9, 8, 10, 9, 11, 13, 15],
};

/* ═══════════════════════════════
   COMPONENT
   ═══════════════════════════════ */

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 8;

  // Notification management state
  const [isNotifModalOpen, setIsNotifModalOpen] = useState(false);
  const [notifForm, setNotifForm] = useState({ message: '', duration: '24' });
  const [activeNotif, setActiveNotif] = useState(null);
  const [notifHistory, setNotifHistory] = useState([]);
  const [timeLeft, setTimeLeft] = useState('');

  // Content Security State
  const [contentSecurity, setContentSecurity] = useState({
    blog: { locked: false, password: '' },
    tutorials: { locked: false, password: '' },
    advanced: { locked: false, password: '' }
  });

  // Check for active notification, history, and security on mount
  useEffect(() => {
    const savedNotif = localStorage.getItem('stocks4u_global_notification');
    if (savedNotif) setActiveNotif(JSON.parse(savedNotif));
    
    const history = localStorage.getItem('stocks4u_notif_history');
    if (history) setNotifHistory(JSON.parse(history));

    const security = localStorage.getItem('stocks4u_content_security');
    if (security) setContentSecurity(JSON.parse(security));
  }, []);

  // Countdown timer for active notification
  useEffect(() => {
    if (!activeNotif) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const diff = activeNotif.expiry - now;

      if (diff <= 0) {
        setActiveNotif(null);
        setTimeLeft('Expired');
        return;
      }

      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);

      setTimeLeft(`${hours}h ${mins}m ${secs}s`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeNotif]);

  useEffect(() => {
    const isAdmin = sessionStorage.getItem('adminAuthenticated');
    if (!isAdmin) {
      navigate('/admin-login');
    }
  }, [navigate]);

  const handleAdminLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    if (onLogout) onLogout();
    navigate('/');
  };

  const handleExport = () => {
    let dataToExport = [];
    let filename = 'stocks4u_data.csv';

    // Decide what to export based on active tab
    if (activeTab === 'users') {
      dataToExport = allUsers;
      filename = `stocks4u_users_${new Date().toLocaleDateString()}.csv`;
    } else if (activeTab === 'subscriptions') {
      dataToExport = revenueByPlan;
      filename = `stocks4u_revenue_${new Date().toLocaleDateString()}.csv`;
    } else if (activeTab === 'analytics') {
      dataToExport = cityData;
      filename = `stocks4u_city_analytics_${new Date().toLocaleDateString()}.csv`;
    } else {
      // Default to traffic data for dashboard
      dataToExport = trafficData;
      filename = `stocks4u_traffic_${new Date().toLocaleDateString()}.csv`;
    }

    if (!dataToExport || dataToExport.length === 0) {
      alert('No data available to export for this section.');
      return;
    }

    try {
      // Extract headers from the first object
      const headers = Object.keys(dataToExport[0]);
      
      // Create CSV rows
      const csvRows = [
        headers.join(','), // header row
        ...dataToExport.map(row => 
          headers.map(fieldName => {
            const value = row[fieldName] ?? '';
            // Escape quotes and wrap in quotes
            const escaped = ('' + value).replace(/"/g, '""');
            return `"${escaped}"`;
          }).join(',')
        )
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported ${dataToExport.length} rows to ${filename}`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  const handleSendNotification = () => {
    setIsNotifModalOpen(true);
  };

  const submitNotification = (e) => {
    e.preventDefault();
    if (notifForm.message && notifForm.message.trim()) {
      const durationHrs = parseInt(notifForm.duration) || 24;
      const timestamp = new Date().getTime();
      const notificationData = {
        id: timestamp,
        message: notifForm.message.trim(),
        timestamp: timestamp,
        duration: durationHrs,
        expiry: timestamp + (durationHrs * 3600000)
      };
      
      localStorage.setItem('stocks4u_global_notification', JSON.stringify(notificationData));
      
      // Update History
      const updatedHistory = [notificationData, ...notifHistory.slice(0, 4)];
      setNotifHistory(updatedHistory);
      localStorage.setItem('stocks4u_notif_history', JSON.stringify(updatedHistory));

      setActiveNotif(notificationData);
      setIsNotifModalOpen(false);
      setNotifForm({ message: '', duration: '24' });
      alert('Notification broadcasted successfully!');
    }
  };

  const handleDeleteNotification = () => {
    if (window.confirm('Are you sure you want to delete the current notification for all users?')) {
      localStorage.removeItem('stocks4u_global_notification');
      setActiveNotif(null);
      alert('Notification removed.');
    }
  };

  /* ── Navigation items ── */
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '💳' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'blog', label: 'Blog Manager', icon: '✍️' },
    { id: 'tutorials', label: 'Tutorial Manager', icon: '🎥' },
    { id: 'advanced-learning', label: 'Advanced Learning', icon: '🎓' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  /* ── Filtered users for Users tab ── */
  const filteredUsers = allUsers.filter(u =>
    u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.plan.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.status.toLowerCase().includes(userSearch.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  /* ── Custom tooltip ── */
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'rgba(15, 15, 40, 0.95)',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '10px',
          padding: '10px 14px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '6px' }}>{label}</p>
          {payload.map((entry, i) => (
            <p key={i} style={{ color: entry.color, fontSize: '0.82rem', fontWeight: 600, margin: '2px 0' }}>
              {entry.name}: {(entry.value / 1000).toFixed(1)}K
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  /* ── Render sparkline ── */
  const renderSparkline = (data, color) => {
    const max = Math.max(...data);
    return (
      <div className="adm-stat-sparkline">
        {data.map((val, i) => (
          <div
            key={i}
            className="adm-spark-bar"
            style={{
              height: `${(val / max) * 100}%`,
              background: `${color}40`,
            }}
          />
        ))}
      </div>
    );
  };

  /* ════════════════════════════
     TAB: DASHBOARD
     ════════════════════════════ */
  const renderDashboard = () => (
    <>
      {/* KPI Stats */}
      <div className="adm-stats-grid">
        <div className="adm-stat-card">
          <div className="adm-stat-accent purple" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Total Users</span>
            <div className="adm-stat-icon-wrap purple">👥</div>
          </div>
          <div className="adm-stat-value">9,350</div>
          <span className="adm-stat-change up">↑ 12.4% this month</span>
          {renderSparkline(sparklines.users, '#8B5CF6')}
        </div>

        <div className="adm-stat-card">
          <div className="adm-stat-accent cyan" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Active Subscriptions</span>
            <div className="adm-stat-icon-wrap cyan">💳</div>
          </div>
          <div className="adm-stat-value">5,150</div>
          <span className="adm-stat-change up">↑ 8.7% this month</span>
          {renderSparkline(sparklines.subs, '#06B6D4')}
        </div>

        <div className="adm-stat-card">
          <div className="adm-stat-accent emerald" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Monthly Revenue</span>
            <div className="adm-stat-icon-wrap emerald">💰</div>
          </div>
          <div className="adm-stat-value">₹7.17L</div>
          <span className="adm-stat-change up">↑ 15.2% this month</span>
          {renderSparkline(sparklines.revenue, '#10B981')}
        </div>

        <div className="adm-stat-card">
          <div className="adm-stat-accent amber" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Page Views</span>
            <div className="adm-stat-icon-wrap amber">👁️</div>
          </div>
          <div className="adm-stat-value">148K</div>
          <span className="adm-stat-change up">↑ 18.4% this month</span>
          {renderSparkline(sparklines.views, '#F59E0B')}
        </div>
      </div>

      {/* Charts row */}
      <div className="adm-charts-row">
        {/* Area chart */}
        <div className="adm-chart-card">
          <div className="adm-chart-header">
            <span className="adm-chart-title">Traffic & Subscriptions</span>
            <div className="adm-chart-legend">
              <span className="adm-legend-item"><span className="adm-legend-dot pink" /> Page Views</span>
              <span className="adm-legend-item"><span className="adm-legend-dot cyan" /> Subscriptions</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trafficData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="admGradViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D946EF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#D946EF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="admGradSubs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="name" stroke="none" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} tickLine={false} />
              <YAxis stroke="none" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} tickLine={false} tickFormatter={(v) => `${v / 1000}K`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="views" name="Page Views" stroke="#D946EF" strokeWidth={2} fill="url(#admGradViews)" />
              <Area type="monotone" dataKey="subs" name="Subscriptions" stroke="#06B6D4" strokeWidth={2} fill="url(#admGradSubs)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Plan distribution donut */}
        <div className="adm-chart-card">
          <div className="adm-chart-header">
            <span className="adm-chart-title">Plan Distribution</span>
          </div>
          <div className="adm-donut-container">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={planDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {planDistribution.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15,15,40,0.95)',
                    border: '1px solid rgba(139,92,246,0.2)',
                    borderRadius: '8px',
                    fontSize: '0.82rem',
                    color: '#fff',
                  }}
                  formatter={(value) => [`${value} users`, '']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="adm-donut-legend">
              {planDistribution.map((item, i) => (
                <div key={i} className="adm-donut-legend-item">
                  <div className="adm-donut-legend-left">
                    <span className="adm-donut-legend-dot" style={{ background: item.color }} />
                    <span className="adm-donut-legend-name">{item.name}</span>
                  </div>
                  <span className="adm-donut-legend-value">{item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="adm-quick-actions">
        <button className="adm-quick-action" onClick={() => window.location.href = '/admin/blog'}>
          <div className="adm-quick-action-icon">✍️</div>
          <div className="adm-quick-action-text">
            <h4>Blog Admin</h4>
            <p>Manage posts & articles</p>
          </div>
        </button>
        <button className="adm-quick-action" onClick={() => window.location.href = '/admin/tutorials'}>
          <div className="adm-quick-action-icon">🎥</div>
          <div className="adm-quick-action-text">
            <h4>Tutorial Admin</h4>
            <p>Post & manage videos</p>
          </div>
        </button>
        <button className="adm-quick-action" onClick={() => window.location.href = '/admin/advanced-learning'}>
          <div className="adm-quick-action-icon">🎓</div>
          <div className="adm-quick-action-text">
            <h4>Advanced Learning</h4>
            <p>Manage expert lessons</p>
          </div>
        </button>
        <button className="adm-quick-action" onClick={handleExport}>
          <div className="adm-quick-action-icon">📥</div>
          <div className="adm-quick-action-text">
            <h4>Export Data</h4>
            <p>Download analytics CSV</p>
          </div>
        </button>
        <button className={`adm-quick-action ${activeNotif ? 'has-active' : ''}`} onClick={handleSendNotification}>
          <div className="adm-quick-action-icon">🔔</div>
          <div className="adm-quick-action-text">
            <h4>{activeNotif ? 'Update Notification' : 'Send Notification'}</h4>
            <p>{activeNotif ? 'Currently broadcasting...' : 'Push to all users'}</p>
          </div>
        </button>

        {activeNotif && (
          <button className="adm-quick-action danger" onClick={handleDeleteNotification}>
            <div className="adm-quick-action-icon">🗑️</div>
            <div className="adm-quick-action-text">
              <h4>Delete Notification</h4>
              <p>Stop broadcast now</p>
            </div>
          </button>
        )}
      </div>

      {/* Active Notification Log/Preview */}
      {(activeNotif || notifHistory.length > 0) && (
        <div className="adm-notif-log-card">
          <div className="adm-notif-log-grid">
            {/* Active Side */}
            <div className="adm-notif-active-side">
              <div className="adm-log-header">
                <span className="adm-log-title">🔴 LIVE BROADCAST</span>
                {activeNotif && <span className="adm-log-timer">{timeLeft} remaining</span>}
              </div>
              
              {activeNotif ? (
                <div className="adm-active-notif-preview">
                  <p>"{activeNotif.message}"</p>
                  <div className="adm-active-notif-meta">
                    <span>Sent: {new Date(activeNotif.timestamp).toLocaleTimeString()}</span>
                    <span>Duration: {activeNotif.duration}h</span>
                  </div>
                </div>
              ) : (
                <div className="adm-no-active-notif">
                  <p>No active notification currently broadcasting.</p>
                </div>
              )}
            </div>

            {/* History Side */}
            <div className="adm-notif-history-side">
              <div className="adm-log-header">
                <span className="adm-log-title">📜 RECENT LOGS</span>
              </div>
              <div className="adm-notif-history-list">
                {notifHistory.length > 0 ? (
                  notifHistory.map((log, idx) => (
                    <div key={idx} className="adm-history-item">
                      <p className="adm-history-msg">{log.message}</p>
                      <span className="adm-history-time">{new Date(log.timestamp).toLocaleDateString()} at {new Date(log.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))
                ) : (
                  <p className="adm-no-history">No history found.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent signups table */}
      <div className="adm-table-card">
        <div className="adm-table-header">
          <span className="adm-table-title">Recent Signups</span>
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td><span className={`adm-plan-badge ${user.plan.toLowerCase()}`}>{user.plan}</span></td>
                  <td>{user.date}</td>
                  <td><span className={`adm-badge ${user.status}`}>{user.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: USERS
     ════════════════════════════ */
  const renderUsers = () => (
    <>
      <div className="adm-table-card">
        <div className="adm-table-header">
          <span className="adm-table-title">All Users ({filteredUsers.length})</span>
          <div className="adm-table-actions">
            <input
              type="text"
              className="adm-search-input"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => { setUserSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Joined</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td><span className={`adm-plan-badge ${user.plan.toLowerCase()}`}>{user.plan}</span></td>
                  <td>{user.date}</td>
                  <td><span className={`adm-badge ${user.status}`}>{user.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="adm-pagination">
            <button className="adm-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>←</button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`adm-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button className="adm-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>→</button>
          </div>
        )}
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: SUBSCRIPTIONS
     ════════════════════════════ */
  const renderSubscriptions = () => (
    <>
      {/* Plan cards */}
      <div className="adm-sub-cards">
        {planDistribution.map((plan, i) => (
          <div key={i} className="adm-sub-card" style={{ borderTop: `3px solid ${plan.color}` }}>
            <div className="adm-sub-plan-name">{plan.name}</div>
            <div className="adm-sub-count">{plan.value.toLocaleString()}</div>
            <div className="adm-sub-revenue">₹{(revenueByPlan[i].revenue / 1000).toFixed(0)}K revenue</div>
          </div>
        ))}
      </div>

      {/* Revenue by plan chart */}
      <div className="adm-chart-card" style={{ marginBottom: '1.75rem' }}>
        <div className="adm-chart-header">
          <span className="adm-chart-title">Revenue by Plan</span>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={revenueByPlan} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="name" stroke="none" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} tickLine={false} />
            <YAxis stroke="none" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} tickLine={false} tickFormatter={(v) => `₹${v / 1000}K`} />
            <Tooltip
              contentStyle={{
                background: 'rgba(15,15,40,0.95)',
                border: '1px solid rgba(139,92,246,0.2)',
                borderRadius: '8px',
                color: '#fff',
              }}
              formatter={(value) => [`₹${(value / 1000).toFixed(0)}K`, 'Revenue']}
            />
            <Bar dataKey="revenue" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Expiring soon */}
      <div className="adm-expiring-alerts">
        <div className="adm-expiring-title">⚠️ Subscriptions Expiring Soon</div>
        <div className="adm-table-wrapper">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Plan</th>
                <th>Expires In</th>
              </tr>
            </thead>
            <tbody>
              {expiringUsers.map((u, i) => (
                <tr key={i}>
                  <td>{u.email}</td>
                  <td><span className={`adm-plan-badge ${u.plan.toLowerCase()}`}>{u.plan}</span></td>
                  <td><span className="adm-badge inactive">{u.expiresIn}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: ANALYTICS
     ════════════════════════════ */
  const renderAnalytics = () => (
    <>
      <div className="adm-analytics-grid">
        {/* Device breakdown */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Device Breakdown</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={deviceData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value" stroke="none">
                {deviceData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: 'rgba(15,15,40,0.95)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '8px', color: '#fff' }}
                formatter={(value) => [`${value}%`, '']}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="adm-donut-legend">
            {deviceData.map((d, i) => (
              <div key={i} className="adm-donut-legend-item">
                <div className="adm-donut-legend-left">
                  <span className="adm-donut-legend-dot" style={{ background: d.color }} />
                  <span className="adm-donut-legend-name">{d.name}</span>
                </div>
                <span className="adm-donut-legend-value">{d.value}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Geographic distribution */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Top Cities</div>
          <div className="adm-progress-list">
            {cityData.map((city, i) => (
              <div key={i} className="adm-progress-item">
                <div className="adm-progress-header">
                  <span className="adm-progress-label">{city.city}</span>
                  <span className="adm-progress-value">{city.users.toLocaleString()} ({city.pct}%)</span>
                </div>
                <div className="adm-progress-bar">
                  <div
                    className="adm-progress-fill"
                    style={{
                      width: `${city.pct * 3.3}%`,
                      background: `linear-gradient(90deg, #8B5CF6, #06B6D4)`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Peak usage hours */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Peak Usage Hours</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={peakHours} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="hour" stroke="none" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} tickLine={false} />
              <YAxis stroke="none" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'rgba(15,15,40,0.95)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '8px', color: '#fff' }}
                formatter={(value) => [`${value}%`, 'Usage']}
              />
              <Bar dataKey="usage" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Feature usage */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Feature Usage</div>
          <div className="adm-feature-list">
            {featureUsage.map((f, i) => (
              <div key={i} className="adm-feature-item">
                <div className="adm-feature-left">
                  <span className="adm-feature-icon">{f.icon}</span>
                  <span className="adm-feature-name">{f.name}</span>
                </div>
                <span className="adm-feature-count">{f.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: BLOG MANAGER
     ════════════════════════════ */
  const renderBlogManager = () => (
    <>
      <div className="adm-blog-overview">
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">📝</div>
          <div className="adm-blog-stat-value">24</div>
          <div className="adm-blog-stat-label">Total Posts</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">👁️</div>
          <div className="adm-blog-stat-value">18.5K</div>
          <div className="adm-blog-stat-label">Total Views</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">💬</div>
          <div className="adm-blog-stat-value">342</div>
          <div className="adm-blog-stat-label">Comments</div>
        </div>
      </div>
      <button className="adm-blog-action-btn" onClick={() => window.location.href = '/admin/blog'}>
        ✍️ Open Blog Admin Panel
      </button>
    </>
  );

  /* ════════════════════════════
     TAB: TUTORIAL MANAGER
     ════════════════════════════ */
  const renderTutorialManager = () => (
    <>
      <div className="adm-blog-overview">
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">🎥</div>
          <div className="adm-blog-stat-value">12</div>
          <div className="adm-blog-stat-label">Total Tutorials</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">🕒</div>
          <div className="adm-blog-stat-value">4.5h</div>
          <div className="adm-blog-stat-label">Total Duration</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">🎓</div>
          <div className="adm-blog-stat-value">1.2K</div>
          <div className="adm-blog-stat-label">Student Enrollments</div>
        </div>
      </div>
      <button className="adm-blog-action-btn" onClick={() => window.location.href = '/admin/tutorials'}>
        🎥 Open Tutorial Admin Panel
      </button>
    </>
  );

  /* ════════════════════════════
     TAB: ADVANCED LEARNING MANAGER
     ════════════════════════════ */
  const renderAdvancedLearningManager = () => (
    <>
      <div className="adm-blog-overview">
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">🎓</div>
          <div className="adm-blog-stat-value">48</div>
          <div className="adm-blog-stat-label">Total Lessons</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">📈</div>
          <div className="adm-blog-stat-value">124K</div>
          <div className="adm-blog-stat-label">Total Views</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">🏆</div>
          <div className="adm-blog-stat-value">85%</div>
          <div className="adm-blog-stat-label">Completion Rate</div>
        </div>
      </div>
      <button className="adm-blog-action-btn" onClick={() => window.location.href = '/admin/advanced-learning'}>
        🎓 Open Advanced Learning Admin
      </button>
    </>
  );

  /* ════════════════════════════
     TAB: SETTINGS
     ════════════════════════════ */
  const [settingsPassword, setSettingsPassword] = useState({ current: '', newPass: '', confirm: '' });
  const [settingsMsg, setSettingsMsg] = useState('');

  const handlePasswordChange = (e) => {
    e.preventDefault();
    const currentStoredPass = localStorage.getItem('stocks4u_admin_pass') || 'admin123';

    if (settingsPassword.newPass !== settingsPassword.confirm) {
      setSettingsMsg('New passwords do not match.');
      return;
    }
    if (settingsPassword.current !== currentStoredPass) {
      setSettingsMsg('Current password is incorrect.');
      return;
    }

    // Save to localStorage
    localStorage.setItem('stocks4u_admin_pass', settingsPassword.newPass);
    setSettingsMsg('Password updated successfully!');
    setSettingsPassword({ current: '', newPass: '', confirm: '' });
  };

  const handleSecurityUpdate = (section, field, value) => {
    const updated = {
      ...contentSecurity,
      [section]: { ...contentSecurity[section], [field]: value }
    };
    setContentSecurity(updated);
    localStorage.setItem('stocks4u_content_security', JSON.stringify(updated));
  };

  const renderSettings = () => (
    <div className="adm-settings-grid">
      {/* Admin profile */}
      <div className="adm-settings-card">
        <h3>Admin Profile</h3>
        <div className="adm-settings-field">
          <label>Admin ID</label>
          <input type="text" value="stocks4u_admin" readOnly />
        </div>
        <div className="adm-settings-field">
          <label>Role</label>
          <input type="text" value="Super Admin" readOnly />
        </div>
        <div className="adm-settings-field">
          <label>Email</label>
          <input type="text" value="admin@stocks4u.in" readOnly />
        </div>
      </div>

      {/* Change password */}
      <div className="adm-settings-card">
        <h3>Change Password</h3>
        <form onSubmit={handlePasswordChange}>
          <div className="adm-settings-field">
            <label>Current Password</label>
            <input
              type="password"
              value={settingsPassword.current}
              onChange={(e) => setSettingsPassword({ ...settingsPassword, current: e.target.value })}
              placeholder="Enter current password"
            />
          </div>
          <div className="adm-settings-field">
            <label>New Password</label>
            <input
              type="password"
              value={settingsPassword.newPass}
              onChange={(e) => setSettingsPassword({ ...settingsPassword, newPass: e.target.value })}
              placeholder="Enter new password"
            />
          </div>
          <div className="adm-settings-field">
            <label>Confirm Password</label>
            <input
              type="password"
              value={settingsPassword.confirm}
              onChange={(e) => setSettingsPassword({ ...settingsPassword, confirm: e.target.value })}
              placeholder="Confirm new password"
            />
          </div>
          {settingsMsg && (
            <p style={{
              fontSize: '0.82rem',
              color: settingsMsg.includes('success') ? '#34d399' : '#f87171',
              marginBottom: '0.75rem',
            }}>{settingsMsg}</p>
          )}
          <button type="submit" className="adm-settings-save-btn">Update Password</button>
        </form>
      </div>

      {/* Content Access Control */}
      <div className="adm-settings-card full-width">
        <h3>Content Access Control (VIP Lock)</h3>
        <p className="adm-settings-hint">Lock specific sections for users. They will need the password you set here to view the content.</p>
        
        <div className="adm-security-list">
          {/* Blog Lock */}
          <div className="adm-security-item">
            <div className="adm-security-info">
              <span className="adm-security-icon">✍️</span>
              <div className="adm-security-text">
                <h4>Blog Section</h4>
                <p>Enable password protection for all blog posts</p>
              </div>
            </div>
            <div className="adm-security-actions">
              <label className="adm-toggle">
                <input 
                  type="checkbox" 
                  checked={contentSecurity.blog.locked} 
                  onChange={(e) => handleSecurityUpdate('blog', 'locked', e.target.checked)}
                />
                <span className="adm-toggle-slider"></span>
              </label>
              {contentSecurity.blog.locked && (
                <input 
                  type="text" 
                  className="adm-lock-input"
                  placeholder="Set Password"
                  value={contentSecurity.blog.password}
                  onChange={(e) => handleSecurityUpdate('blog', 'password', e.target.value)}
                />
              )}
            </div>
          </div>

          {/* Tutorials Lock */}
          <div className="adm-security-item">
            <div className="adm-security-info">
              <span className="adm-security-icon">🎥</span>
              <div className="adm-security-text">
                <h4>Tutorials Section</h4>
                <p>Protect instructional videos and guides</p>
              </div>
            </div>
            <div className="adm-security-actions">
              <label className="adm-toggle">
                <input 
                  type="checkbox" 
                  checked={contentSecurity.tutorials.locked} 
                  onChange={(e) => handleSecurityUpdate('tutorials', 'locked', e.target.checked)}
                />
                <span className="adm-toggle-slider"></span>
              </label>
              {contentSecurity.tutorials.locked && (
                <input 
                  type="text" 
                  className="adm-lock-input"
                  placeholder="Set Password"
                  value={contentSecurity.tutorials.password}
                  onChange={(e) => handleSecurityUpdate('tutorials', 'password', e.target.value)}
                />
              )}
            </div>
          </div>

          {/* Advanced Lock */}
          <div className="adm-security-item">
            <div className="adm-security-info">
              <span className="adm-security-icon">🎓</span>
              <div className="adm-security-text">
                <h4>Advanced Learning</h4>
                <p>Lock expert strategies and premium data</p>
              </div>
            </div>
            <div className="adm-security-actions">
              <label className="adm-toggle">
                <input 
                  type="checkbox" 
                  checked={contentSecurity.advanced.locked} 
                  onChange={(e) => handleSecurityUpdate('advanced', 'locked', e.target.checked)}
                />
                <span className="adm-toggle-slider"></span>
              </label>
              {contentSecurity.advanced.locked && (
                <input 
                  type="text" 
                  className="adm-lock-input"
                  placeholder="Set Password"
                  value={contentSecurity.advanced.password}
                  onChange={(e) => handleSecurityUpdate('advanced', 'password', e.target.value)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Tab title map ── */
  const tabTitles = {
    dashboard: { title: 'Dashboard Overview', sub: 'Monitor your platform KPIs and analytics' },
    users: { title: 'User Management', sub: 'View and manage all registered users' },
    subscriptions: { title: 'Subscriptions', sub: 'Plan analytics and revenue tracking' },
    analytics: { title: 'Analytics', sub: 'Deep dive into platform usage data' },
    blog: { title: 'Blog Manager', sub: 'Manage blog posts and content' },
    tutorials: { title: 'Tutorial Manager', sub: 'Manage video tutorials and learning content' },
    'advanced-learning': { title: 'Advanced Learning', sub: 'Manage advanced courses and expert videos' },
    settings: { title: 'Settings', sub: 'Admin profile and system configuration' },
  };

  /* ── Tab content map ── */
  const tabRenderers = {
    dashboard: renderDashboard,
    users: renderUsers,
    subscriptions: renderSubscriptions,
    analytics: renderAnalytics,
    blog: renderBlogManager,
    tutorials: renderTutorialManager,
    'advanced-learning': renderAdvancedLearningManager,
    settings: renderSettings,
  };

  /* ═══════════════════════════════
     RENDER
     ═══════════════════════════════ */
  return (
    <div className="admin-dashboard-wrapper">
      {/* Sidebar overlay (mobile) */}
      <div
        className={`adm-sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── SIDEBAR ── */}
      <aside className={`adm-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="adm-sidebar-header">
          <div className="adm-logo-icon">📊</div>
          <div>
            <div className="adm-logo-text">Stocks4U</div>
            <span className="adm-logo-sub">Admin Portal</span>
          </div>
        </div>

        <nav className="adm-nav">
          <div className="adm-nav-section">
            <div className="adm-nav-label">Main</div>
            {navItems.slice(0, 4).map((item) => (
              <button
                key={item.id}
                className={`adm-nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              >
                <span className="adm-nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          <div className="adm-nav-section">
            <div className="adm-nav-label">Tools</div>
            {navItems.slice(4).map((item) => (
              <button
                key={item.id}
                className={`adm-nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              >
                <span className="adm-nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        <div className="adm-sidebar-footer">
          <div className="adm-user-card">
            <div className="adm-user-avatar">SA</div>
            <div>
              <div className="adm-user-name">Super Admin</div>
              <div className="adm-user-role">stocks4u_admin</div>
            </div>
          </div>
          <button className="adm-logout-btn" onClick={handleAdminLogout}>
            🚪 Log Out
          </button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div className="adm-main">
        {/* Top bar */}
        <div className="adm-topbar">
          <button className="adm-mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            ☰
          </button>
          <div className="adm-topbar-left">
            <h1>{tabTitles[activeTab]?.title}</h1>
            <p>{tabTitles[activeTab]?.sub}</p>
          </div>
          <div className="adm-topbar-actions">
            <button className="adm-topbar-btn" onClick={handleExport}>
              <span className="adm-topbar-btn-icon">📥</span>
              <span>Export</span>
            </button>
            <button className="adm-topbar-btn" onClick={() => window.location.reload()}>
              <span className="adm-topbar-btn-icon">🔄</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="adm-content">
          {tabRenderers[activeTab]?.()}
        </div>

        {/* Notification Modal */}
        <Modal isOpen={isNotifModalOpen} onClose={() => setIsNotifModalOpen(false)}>
          <div className="adm-notif-modal">
            <div className="adm-modal-header">
              <span className="adm-modal-icon">🔔</span>
              <div>
                <h3>Broadcast Notification</h3>
                <p>Send a global message to all active users</p>
              </div>
            </div>

            <form onSubmit={submitNotification}>
              <div className="adm-form-group">
                <label>Message Content</label>
                <textarea
                  placeholder="Enter notification message..."
                  value={notifForm.message}
                  onChange={(e) => setNotifForm({ ...notifForm, message: e.target.value })}
                  required
                />
                <span className="adm-form-hint">Tip: Keep it concise for better visibility on mobile.</span>
              </div>

              <div className="adm-form-group">
                <label>Display Duration</label>
                <select
                  value={notifForm.duration}
                  onChange={(e) => setNotifForm({ ...notifForm, duration: e.target.value })}
                >
                  <option value="1">1 Hour</option>
                  <option value="4">4 Hours</option>
                  <option value="8">8 Hours</option>
                  <option value="12">12 Hours</option>
                  <option value="24">24 Hours (1 Day)</option>
                  <option value="48">48 Hours (2 Days)</option>
                  <option value="168">1 Week</option>
                </select>
              </div>

              <div className="adm-modal-actions">
                <button type="button" className="adm-btn-secondary" onClick={() => setIsNotifModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="adm-btn-primary">
                  {activeNotif ? 'Update Broadcast' : 'Push Notification'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AdminDashboard;
